import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { refreshCurrentLegacyPage } from '@economic/react-app';

import internalApiClient from '../../../../../../shared/util/api/internalApiClient';
import MoneyflowInvalidOfferDialogComponent from './MoneyflowInvalidOfferDialog.Component';
import MoneyflowLoadingDialog from './MoneyflowLoadingDialog';
import MoneyflowReviewOfferDialogComponent from './MoneyflowReviewOfferDialog.Component';
import useQuery from '../../../../../../shared/hooks/useQuery';
import { InvoicingMessageType } from '../types';
import { MoneyFlowCompany } from '../../../../../settings/integrations/moneyflow/types';
import { MoneyflowOfferOverviewResult, MoneyflowOfferStatusEnum } from '../../../../../../../types/internal-api';

import { useLocalization } from '../../../../../../shared/components/l10n/l10n';
import { usePostQuery } from '../../../../../../shared/hooks/usePostQuery';

export default function MoneyflowReviewOfferDialog() {
    const location = useLocation();
    const { invoiceDraftId } = location.state || ({} as any);
    const loadingTexts = useLocalization(textIds);
    const navigate = useNavigate();
    const { data: companyData, loading: loadingCompany } = useQuery<MoneyFlowCompany>('/moneyflow/company', {});
    const { loading: updatingOffer } = usePostQuery('moneyflow/offer/update', { invoiceDraftId });
    const { data: offerData, loading: loadingOffer, refetch: refetchOffer } = useQuery<MoneyflowOfferOverviewResult>(
        `/moneyflow/offer/invoice/${invoiceDraftId}`,
        {},
        { skip: updatingOffer }
    );

    const open = location.hash === '#moneyflow-review-offer';

    const loading = loadingCompany || loadingTexts || loadingOffer || updatingOffer;
    const bankInfo = { regNo: companyData?.bankRegNumber, accountNo: companyData?.bankAccountNumber };
    const hide = () => navigate(location.pathname);

    const toggle = async isToggled => {
        await internalApiClient.post('/moneyflow/offer/toggle', { invoiceDraftId, isToggled });
        refetchOffer();
        refreshCurrentLegacyPage('content');
    };

    if (loading) {
        return <MoneyflowLoadingDialog open={open} hide={hide} />;
    }

    if (offerData && offerData.offerStatus !== MoneyflowOfferStatusEnum.Valid) {
        return (
            <MoneyflowInvalidOfferDialogComponent
                open={open}
                messages={[{ type: InvoicingMessageType.Info, message: offerData.info }]}
                hide={hide}
            />
        );
    }

    return (
        <MoneyflowReviewOfferDialogComponent
            open={open}
            hide={hide}
            invoiceDraftId={invoiceDraftId}
            offer={offerData}
            onToggle={toggle}
            paymentBankAccount={bankInfo}
        />
    );
}

const textIds = [
    'FORM_BOOKANDSEND_ERRORS_UNKNOWN_ERROR',
    'FORM_BOOKANDSEND_MONEYFLOW_AMOUNT_RECEIVED',
    'FORM_BOOKANDSEND_MONEYFLOW_FEE',
    'FORM_BOOKANDSEND_MONEYFLOW_FEE_DISCOUNT',
    'FORM_BOOKANDSEND_MONEYFLOW_HEADER_NOTQUALIFIED',
    'FORM_BOOKANDSEND_MONEYFLOW_NET_AMOUNT',
    'FORM_BOOKANDSEND_MONEYFLOW_PAYMENTOVERVIEW',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEWOFFER_AGREEMENT',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEWOFFER_BANKINFO',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEWOFFER_EXPLANATION',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEWOFFER_AGREEMENT_LINK_TEXT',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEWOFFER_WIDGET_ACTIVATE',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEWOFFER_BOOK_AND_SEND',
    'FORM_BOOKANDSEND_MONEYFLOW_REVIEW_OFFER_HEADER',
    'FORM_BOOKANDSEND_MONEYFLOW_VAT',
    'FORM_BOOKANDSEND_MONEYFLOW_VIEWAGREEMENT',
    'FORM_BOOKANDSEND_MONEYFLOW_LOADING_HEADER',
    'SHARED_CONFIRM_CLOSEWINDOW',
    'SHARED_FORM_OK',
];
